/**
 * Google Books API Service
 * Fetches book metadata from Google Books API (no API key required for basic searches)
 */

const GOOGLE_BOOKS_API = 'https://www.googleapis.com/books/v1/volumes';

// Optional API key. Without one, requests use Google's shared anonymous quota
// (per-IP, easily exhausted → HTTP 429). Set VITE_GOOGLE_BOOKS_API_KEY to get a
// dedicated 1,000 req/day quota. Get a key at https://console.cloud.google.com/
// (enable the "Books API").
const GOOGLE_BOOKS_API_KEY = import.meta.env.VITE_GOOGLE_BOOKS_API_KEY as string | undefined;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch from the Google Books API with the optional API key applied and
 * automatic retry/backoff on 429 (rate limit) responses.
 */
async function booksFetch(url: string, maxRetries = 2): Promise<Response> {
  const withKey = GOOGLE_BOOKS_API_KEY
    ? `${url}${url.includes('?') ? '&' : '?'}key=${GOOGLE_BOOKS_API_KEY}`
    : url;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const response = await fetch(withKey);
    if (response.status !== 429) {
      return response;
    }
    if (attempt < maxRetries) {
      // Exponential backoff: 500ms, 1000ms, ...
      await sleep(500 * Math.pow(2, attempt));
    } else {
      throw new Error(
        'Google Books is rate-limiting requests (429). Please wait a moment and try again' +
          (GOOGLE_BOOKS_API_KEY ? '.' : ', or configure a Google Books API key.')
      );
    }
  }
  // Unreachable, but keeps TypeScript happy.
  return fetch(withKey);
}

export interface BookInfo {
  id: string;
  title: string;
  subtitle?: string;
  authors?: string[];
  publisher?: string;
  publishedDate?: string;
  description?: string;
  pageCount?: number;
  categories?: string[];
  thumbnail?: string;
  isbn10?: string;
  isbn13?: string;
  language?: string;
  previewLink?: string;
  infoLink?: string;
}

interface GoogleBooksVolume {
  id: string;
  volumeInfo: {
    title: string;
    subtitle?: string;
    authors?: string[];
    publisher?: string;
    publishedDate?: string;
    description?: string;
    pageCount?: number;
    categories?: string[];
    imageLinks?: {
      thumbnail?: string;
      smallThumbnail?: string;
    };
    industryIdentifiers?: Array<{
      type: string;
      identifier: string;
    }>;
    language?: string;
    previewLink?: string;
    infoLink?: string;
  };
}

interface GoogleBooksResponse {
  totalItems: number;
  items?: GoogleBooksVolume[];
}

/**
 * Search for books by query (title, author, ISBN, etc.)
 */
export async function searchBooks(query: string, maxResults: number = 10): Promise<BookInfo[]> {
  if (!query.trim()) {
    return [];
  }

  try {
    const params = new URLSearchParams({
      q: query,
      maxResults: String(maxResults),
      printType: 'books',
    });

    const response = await booksFetch(`${GOOGLE_BOOKS_API}?${params}`);

    if (!response.ok) {
      throw new Error(`Google Books API error: ${response.status}`);
    }

    const data: GoogleBooksResponse = await response.json();

    if (!data.items) {
      return [];
    }

    return data.items.map(volumeToBookInfo);
  } catch (error) {
    console.error('Error searching Google Books:', error);
    throw error;
  }
}

/**
 * Search by ISBN (10 or 13 digit)
 */
export async function searchByISBN(isbn: string): Promise<BookInfo | null> {
  const cleanISBN = isbn.replace(/[-\s]/g, '');

  if (!/^\d{10}$|^\d{13}$/.test(cleanISBN)) {
    throw new Error('Invalid ISBN format. Must be 10 or 13 digits.');
  }

  const results = await searchBooks(`isbn:${cleanISBN}`, 1);
  return results.length > 0 ? results[0] : null;
}

/**
 * Search by title
 */
export async function searchByTitle(title: string, maxResults: number = 10): Promise<BookInfo[]> {
  return searchBooks(`intitle:${title}`, maxResults);
}

/**
 * Search by author
 */
export async function searchByAuthor(author: string, maxResults: number = 10): Promise<BookInfo[]> {
  return searchBooks(`inauthor:${author}`, maxResults);
}

/**
 * Get book details by Google Books volume ID
 */
export async function getBookById(volumeId: string): Promise<BookInfo | null> {
  try {
    const response = await booksFetch(`${GOOGLE_BOOKS_API}/${volumeId}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Google Books API error: ${response.status}`);
    }

    const volume: GoogleBooksVolume = await response.json();
    return volumeToBookInfo(volume);
  } catch (error) {
    console.error('Error fetching book details:', error);
    throw error;
  }
}

/**
 * Convert Google Books volume to BookInfo
 */
function volumeToBookInfo(volume: GoogleBooksVolume): BookInfo {
  const info = volume.volumeInfo;
  const identifiers = info.industryIdentifiers || [];

  return {
    id: volume.id,
    title: info.title,
    subtitle: info.subtitle,
    authors: info.authors,
    publisher: info.publisher,
    publishedDate: info.publishedDate,
    description: info.description,
    pageCount: info.pageCount,
    categories: info.categories,
    thumbnail: info.imageLinks?.thumbnail?.replace('http:', 'https:'),
    isbn10: identifiers.find(i => i.type === 'ISBN_10')?.identifier,
    isbn13: identifiers.find(i => i.type === 'ISBN_13')?.identifier,
    language: info.language,
    previewLink: info.previewLink,
    infoLink: info.infoLink,
  };
}

/**
 * Format authors as a string
 */
export function formatAuthors(authors?: string[]): string {
  if (!authors || authors.length === 0) {
    return 'Unknown Author';
  }
  if (authors.length === 1) {
    return authors[0];
  }
  if (authors.length === 2) {
    return `${authors[0]} and ${authors[1]}`;
  }
  return `${authors[0]} et al.`;
}

/**
 * Format publication date
 */
export function formatPublishedDate(date?: string): string {
  if (!date) return '';

  // Handle various date formats from Google Books
  if (/^\d{4}$/.test(date)) {
    return date; // Just year
  }
  if (/^\d{4}-\d{2}$/.test(date)) {
    const [year, month] = date.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  return date;
}
